import React from "react";
import { Trash2, Info, Printer } from "lucide-react";
import SearchableSelect from "../SearchableSelect";

export default function DataBarcodeLabelTable({
  data = [],
  loading = false,
  onPrint,
  onDetail,
  onDelete,
  currentPage = 1,
  totalPages = 1,
  maxPages,
  onPageChange,
  itemsPerPage = 10,
  onItemsPerPageChange,
  totalItems = 0,
  totalEntries,
  startEntry,
  endEntry,
}) {
  const actualTotal = totalEntries !== undefined ? totalEntries : totalItems;
  const actualMaxPages = maxPages !== undefined ? maxPages : totalPages;
  const actualStart =
    startEntry !== undefined
      ? startEntry
      : actualTotal === 0
      ? 0
      : (currentPage - 1) * itemsPerPage + 1;
  const actualEnd =
    endEntry !== undefined
      ? endEntry
      : Math.min(currentPage * itemsPerPage, actualTotal);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const d = new Date(dateString);
      const day = d.getDate();
      const months = [
        "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
        "Jul", "Agu", "Sep", "Okt", "Nov", "Des"
      ];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200">
              <th className="px-6 py-4 text-center">Aksi</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Nama Label</th>
              <th className="px-6 py-4">Ukuran Label</th>
              <th className="px-6 py-4 text-center">Produk</th>
              <th className="px-6 py-4 text-center">Total Label</th>
              <th className="px-6 py-4">Tanggal Dibuat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/70 text-sm text-slate-700">
            {loading ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-slate-400">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Memuat data label barcode...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-slate-400">
                  Belum ada label barcode yang dibuat. Klik <strong className="text-slate-600">"+ Buat Label Barcode"</strong> untuk mulai.
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50/80 transition-colors"
                >
                  {/* Aksi */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onPrint && onPrint(item)}
                        className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                        title="Unduh / Cetak Label"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete && onDelete(item)}
                        className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                        title="Hapus Label"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDetail && onDetail(item)}
                        className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                        title="Detail Label"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                        item.status === "Disusun"
                          ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                          : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>

                  {/* Nama Label */}
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                    {item.name}
                  </td>

                  {/* Ukuran Label */}
                  <td className="px-6 py-4 text-sm text-slate-600 font-normal">
                    {item.size}
                  </td>

                  {/* Jumlah Produk */}
                  <td className="px-6 py-4 text-sm text-slate-600 text-center font-normal">
                    {item.product_count || (item.items ? item.items.length : 0)}
                  </td>

                  {/* Total Label */}
                  <td className="px-6 py-4 text-sm text-slate-600 text-center font-normal">
                    {item.total_labels || 0}
                  </td>

                  {/* Tanggal Dibuat */}
                  <td className="px-6 py-4 text-xs text-slate-500 font-normal">
                    {formatDate(item.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Container */}
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">
            Showing{" "}
            <span className="font-medium text-slate-700">{actualStart}</span> to{" "}
            <span className="font-medium text-slate-700">{actualEnd}</span> of{" "}
            <span className="font-medium text-slate-700">{actualTotal}</span> entries
          </span>
          <SearchableSelect
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            value={itemsPerPage}
            onChange={(value) => onItemsPerPageChange && onItemsPerPageChange(Number(value))}
            placeholder="Items per page"
            displayKey="label"
            valueKey="value"
            className="w-20"
            searchable={false}
            size="sm"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onPageChange && onPageChange(1)}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ‹‹
          </button>
          <button
            onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ‹
          </button>

          {Array.from({ length: Math.min(3, actualMaxPages) }, (_, i) => {
            let p = currentPage;
            if (currentPage === 1) p = 1;
            else if (currentPage === actualMaxPages) p = actualMaxPages - 2;
            else p = currentPage - 1;

            if (actualMaxPages < 3) p = 1;

            const pageNum = p + i;
            if (pageNum > actualMaxPages || pageNum < 1) return null;

            return (
              <button
                key={pageNum}
                onClick={() => onPageChange && onPageChange(pageNum)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  currentPage === pageNum
                    ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                    : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() =>
              onPageChange && onPageChange(Math.min(actualMaxPages, currentPage + 1))
            }
            disabled={currentPage === actualMaxPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ›
          </button>
          <button
            onClick={() => onPageChange && onPageChange(actualMaxPages)}
            disabled={currentPage === actualMaxPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ››
          </button>
        </div>
      </div>
    </div>
  );
}
